import Checkbox from '@/components/Checkbox/Checkbox';
import Popup from '@/components/Popup/Popup';
import Switch from '@/components/Switch/Switch';
import {
	dashboardRemoveAllFromSaveList,
	dashboardSetQuery
} from '@/modules/dashboard/dashboardActions';
import { DropDown } from '@/resources/assets/images';
import type { RootState } from '@/rootReducer';
import { sendAnalytics } from '@/services/analyticsService';
import { fromJS } from 'immutable';
import React, { useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import {
	BusinessQuery,
	Filter,
	FilterQueryType,
	FilterType
} from '../../../dashboard/dashboardTypes';

import './styles.scss';
import './dropdownStyles.scss';
import { useBusinessList } from '../../hooks/useBusinessLists';
import { useSequencesContext } from '../../SequencesContext';

export default function Filter() {
	const { fetchBusinessesInLists } = useBusinessList();
	const { setBusinessesScrollPage, setHasMoreBusinessesToFetch } =
		useSequencesContext();

	const [revertHasEmail, setRevertHasEmail] = useState(true);
	const [shouldRefetch, setShouldRefetch] = useState(false);
	const [yelpReviewsFilterMin, setYelpReviewsFilterMin] = useState<
		number | string
	>('');
	const [yelpReviewsFilterMax, setYelpReviewsFilterMax] = useState<
		number | string
	>('');
	const [showAdvanced, setShowAdvanced] = useState(false);

	const [query, user] = useSelector(
		(state: RootState) => [
			state.dashboard.get('query').toJS(),
			state.user.getIn(['authorize', 'user']).toJS()
		],
		shallowEqual
	);

	const dispatch = useDispatch();

	const yelpReviewsApplyButtonDisabledCheck =
		(yelpReviewsFilterMin === '' && yelpReviewsFilterMax === '') ||
		(typeof yelpReviewsFilterMin === 'number' &&
			typeof yelpReviewsFilterMax === 'number' &&
			yelpReviewsFilterMin > 0 &&
			yelpReviewsFilterMax < yelpReviewsFilterMin);

	const removeAllFromSaveList = () =>
		dispatch(dashboardRemoveAllFromSaveList());

	const setQuery = (q: BusinessQuery) => {
		removeAllFromSaveList();
		dispatch(dashboardSetQuery(q));
		setShouldRefetch(!shouldRefetch);
		setBusinessesScrollPage(1);
		setHasMoreBusinessesToFetch(true);
	};

	useEffect(() => {
		fetchBusinessesInLists();
	}, [shouldRefetch]);

	const yelpReviewsFilterChange = (min: number, max?: number) => {
		const queryNew = fromJS(query).toJS();

		queryNew.minYelpReviews = min;
		queryNew.maxYelpReviews = max;

		queryNew.limit = 50;

		queryNew.skip = 0;

		queryNew.queryType = FilterQueryType.FILTER;

		setQuery(queryNew);

		sendAnalytics({
			type: 'track',
			userId: user.email,
			eventName: 'User changed filters',
			properties: {
				filterChanged: 'Yelp Reviews',
				query: queryNew
			}
		});
	};

	const yelpReviewsApplyButtonClickHandler = () => {
		if (yelpReviewsApplyButtonDisabledCheck) {
			return;
		}

		let minValue = yelpReviewsFilterMin as number | string | undefined;
		let maxValue = yelpReviewsFilterMax as number | string | undefined;

		if (minValue === '') {
			minValue = 0;
		}

		if (maxValue === '') {
			maxValue = undefined;
		}

		yelpReviewsFilterChange(minValue as number, maxValue as number);
	};

	const yelpReviewsClearButtonClickHandler = () => {
		setYelpReviewsFilterMax('');
		setYelpReviewsFilterMin('');
	};

	const multiSelectOnChange = (filter: Filter) => {
		const queryNew: BusinessQuery = fromJS(query).toJS();

		if (filter.value === 'nonChains') {
			if (filter.type === FilterType.INCLUDES) {
				queryNew.nonChains = true;

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Non-Chain',
							value: 'Included',
							query: queryNew
						}
					});
				}
			} else {
				delete queryNew.nonChains;
				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Non-Chain',
							value: 'Neutral',
							query: queryNew
						}
					});
				}
			}
		}

		if (revertHasEmail) {
			queryNew.revertIncludeEmailsYes = true;
		} else {
			queryNew.revertIncludeEmailsYes = false;
		}

		if (filter.value === 'includeEmailsYes') {
			if (filter.type === FilterType.INCLUDES) {
				queryNew.includeEmailsYes = true;
				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Has email',
							value: 'Included',
							query: queryNew
						}
					});
				}
			} else {
				delete queryNew.includeEmailsYes;

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Has email',
							value: 'Neutral',
							query: queryNew
						}
					});
				}
			}
		}

		if (filter.value === 'includeWebsitesYes') {
			if (filter.type === FilterType.INCLUDES) {
				queryNew.includeWebsitesYes = true;
				delete queryNew.includeWebsitesNo;
				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Has Website',
							value: 'Included',
							query: queryNew
						}
					});
				}
			} else {
				delete queryNew.includeWebsitesYes;
				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Has Website',
							value: 'Neutral',
							query: queryNew
						}
					});
				}
			}
		}
		if (filter.value === 'includeWebsitesNo') {
			if (filter.type === FilterType.INCLUDES) {
				queryNew.includeWebsitesNo = true;
				delete queryNew.includeWebsitesYes;
				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Does not have Website',
							value: 'Included',
							query: queryNew
						}
					});
				}
			} else {
				delete queryNew.includeWebsitesNo;
				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Does not have Website',
							value: 'Neutral',
							query: queryNew
						}
					});
				}
			}
		}

		if (filter.value === 'includePOIs') {
			if (filter.type === FilterType.INCLUDES) {
				queryNew.includePOIs = true;

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Has staff info',
							value: 'Included',
							query: queryNew
						}
					});
				}
			} else {
				delete queryNew.includePOIs;
				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Has staff info',
							value: 'Neutral',
							query: queryNew
						}
					});
				}
			}
		}

		if (filter.value === 'includeFBMessenger') {
			if (filter.type === FilterType.INCLUDES) {
				queryNew.includeFBMessenger = true;

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Has Facebook Messenger',
							value: 'Included',
							query: queryNew
						}
					});
				}
			} else {
				delete queryNew.includeFBMessenger;
				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Has Facebook Messenger',
							value: 'Neutral',
							query: queryNew
						}
					});
				}
			}
		}

		if (filter.value === 'includeIGMessenger') {
			if (filter.type === FilterType.INCLUDES) {
				queryNew.includeIGMessenger = true;

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Has Instagram Messenger',
							value: 'Included',
							query: queryNew
						}
					});
				}
			} else {
				delete queryNew.includeIGMessenger;
				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Has Instagram Messenger',
							value: 'Neutral',
							query: queryNew
						}
					});
				}
			}
		}

		if (filter.value === 'includePhoneNumber') {
			if (filter.type === FilterType.INCLUDES) {
				queryNew.includePhoneNumber = true;

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Has Phone Number',
							value: 'Included',
							query: queryNew
						}
					});
				}
			} else {
				delete queryNew.includePhoneNumber;
				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Has Phone Number',
							value: 'Neutral',
							query: queryNew
						}
					});
				}
			}
		}

		if (filter.value === 'haveRE2') {
			if (filter.type === FilterType.INCLUDES) {
				queryNew.haveRE2 = true;
			} else {
				delete queryNew.haveRE2;
			}
		}

		if (filter.value === 'contacted') {
			if (filter.type === FilterType.INCLUDES) {
				queryNew.contactedYes = true;
				delete queryNew.contactedNo;

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Contacted',
							value: 'Included',
							query: queryNew
						}
					});
				}
			} else if (filter.type === FilterType.EXCLUDES) {
				queryNew.contactedNo = true;
				delete queryNew.contactedYes;

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Contacted',
							value: 'Excluded',
							query: queryNew
						}
					});
				}
			} else {
				delete queryNew.contactedYes;
				delete queryNew.contactedNo;

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Contacted',
							value: 'Neutral',
							query: queryNew
						}
					});
				}
			}
		}

		if (filter.value === 'scheduled') {
			if (filter.type === FilterType.INCLUDES) {
				queryNew.scheduledYes = true;
				delete queryNew.scheduledNo;

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Scheduled',
							value: 'Included',
							query: queryNew
						}
					});
				}
			} else if (filter.type === FilterType.EXCLUDES) {
				queryNew.scheduledNo = true;
				delete queryNew.scheduledYes;

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Scheduled',
							value: 'Excluded',
							query: queryNew
						}
					});
				}
			} else {
				delete queryNew.scheduledYes;
				delete queryNew.scheduledNo;

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Scheduled',
							value: 'Neutral',
							query: queryNew
						}
					});
				}
			}
		}

		if (filter.value === 'lead') {
			if (filter.type === FilterType.INCLUDES) {
				queryNew.lead = true;

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Lead',
							value: 'Included',
							query: queryNew
						}
					});
				}
			} else if (filter.type === FilterType.EXCLUDES) {
				queryNew.lead = false;

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Lead',
							value: 'Excluded',
							query: queryNew
						}
					});
				}
			} else {
				delete queryNew.lead;

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Lead',
							value: 'Neutral',
							query: queryNew
						}
					});
				}
			}
		}

		if (filter.value === 'notesSaved') {
			if (filter.type === FilterType.INCLUDES) {
				queryNew.notesSaved = true;

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Notes Saved',
							value: 'Included',
							query: queryNew
						}
					});
				}
			} else {
				delete queryNew.notesSaved;

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Notes Saved',
							value: 'Neutral',
							query: queryNew
						}
					});
				}
			}
		}

		if (filter.value === 'countTwoTen') {
			if (filter.type === FilterType.INCLUDES) {
				queryNew.countTwoTen = true;

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Locations: 2-10',
							value: 'Included',
							query: queryNew
						}
					});
				}
			} else {
				delete queryNew.countTwoTen;

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Locations: 2-10',
							value: 'Neutral',
							query: queryNew
						}
					});
				}
			}
		}

		if (filter.value === 'countOne') {
			if (filter.type === FilterType.INCLUDES) {
				queryNew.countOne = true;

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Locations: 1',
							value: 'Included',
							query: queryNew
						}
					});
				}
			} else {
				delete queryNew.countOne;

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Locations: 1',
							value: 'Neutral',
							query: queryNew
						}
					});
				}
			}
		}

		if (filter.value === 'countTenPlus') {
			if (filter.type === FilterType.INCLUDES) {
				queryNew.countTenPlus = true;

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Locations: 10+',
							value: 'Included',
							query: queryNew
						}
					});
				}
			} else {
				delete queryNew.countTenPlus;

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Locations: 10+',
							value: 'Neutral',
							query: queryNew
						}
					});
				}
			}
		}

		if (!queryNew.count) {
			queryNew.count = true;
		}

		queryNew.limit = 50;

		queryNew.skip = 0;

		queryNew.queryType = FilterQueryType.FILTER;

		setQuery(queryNew);
	};

	return (
		<div className="dashboard-container--controls-filter relative ml-4">
			<Popup
				text={
					<div
						className="flex w-full items-center"
						id="filters-dropdown"
					>
						Filters
						{(() => {
							const filterCount =
								0 +
								((query?.minDistance &&
									query?.minDistance !== 0) ||
								(query?.maxDistance &&
									query?.maxDistance !== 40)
									? 1
									: 0) +
								(query?.haveRE2 ? 1 : 0) +
								(query?.includeWebsitesYes ? 1 : 0) +
								(query?.includeWebsitesNo ? 1 : 0) +
								(query?.scheduledYes ? 1 : 0) +
								(query?.scheduledNo ? 1 : 0) +
								(typeof query?.minYelpReviews === 'number' ||
								typeof query?.maxYelpReviews === 'number'
									? 1
									: 0) +
								(query?.contactedYes ? 1 : 0) +
								(query?.includePOIs ? 1 : 0) +
								(query?.includeFBMessenger ? 1 : 0) +
								(query?.includeIGMessenger ? 1 : 0) +
								(query?.includeEmailsYes ? 1 : 0) +
								(query?.includeEmailsNo ? 1 : 0) +
								(query?.includePhoneNumber ? 1 : 0) +
								(query?.nonChains ? 1 : 0) +
								(query?.contactedNo ? 1 : 0) +
								(query?.lead !== undefined &&
								query?.lead !== null
									? 1
									: 0) +
								(query?.notesSaved ? 1 : 0) +
								(query?.countTwoTen ? 1 : 0) +
								(query?.countOne ? 1 : 0) +
								(query?.countTenPlus ? 1 : 0);

							return (
								filterCount > 0 && (
									<span className="dashboard-container--controls-filter-count ml-auto flex items-center justify-center rounded-xl bg-orange">
										<strong className="pb-0.5 pr-0.5 text-xs font-bold text-black">
											{filterCount}
										</strong>
									</span>
								)
							);
						})()}
					</div>
				}
				img={
					<DropDown
						width={14}
						height={7}
						className="ml-3 text-light-grey"
					/>
				}
				containerClass="dashboard-container--controls-filter--container px-3 py-7 2xl:px-5 2xl:py-10"
				contentClass="filters-content"
			>
				<div className="flex w-full flex-col">
					<div className="flex h-full w-full flex-col justify-between">
						<span className="text-xl font-light text-white">
							Business info
						</span>
						<div className="dashboard-container--controls-filter--row font-light">
							<Checkbox
								variant="chip"
								className="mr-3 mt-3"
								onChange={e =>
									multiSelectOnChange({
										value: 'nonChains',
										type: e.target.checked
											? FilterType.INCLUDES
											: FilterType.EXCLUDES
									})
								}
								defaultChecked={query?.nonChains}
								text="Non-chain"
								hoverText="Filter for businesses that are not part of any national chains"
							/>
							<Checkbox
								variant="chip"
								className="mr-3 mt-3"
								id="has-email-checkbox"
								onChange={e => {
									e.target.checked
										? setRevertHasEmail(false)
										: setRevertHasEmail(true);
									multiSelectOnChange({
										value: 'includeEmailsYes',
										type: e.target.checked
											? FilterType.INCLUDES
											: FilterType.EXCLUDES
									});
								}}
								defaultChecked={query?.includeEmailsYes}
								text="Has email"
								hoverText="Filter for businesses that have an email"
							/>

							<Checkbox
								variant="chip"
								className="mr-3 mt-3"
								onChange={e =>
									multiSelectOnChange({
										value: 'includeFBMessenger',
										type: e.target.checked
											? FilterType.INCLUDES
											: FilterType.EXCLUDES
									})
								}
								defaultChecked={query?.includeFBMessenger}
								text="Has Facebook Messenger"
								hoverText="Filter for businesses that have a Facebook Messenger account"
							/>

							<Checkbox
								variant="chip"
								className="mr-3 mt-3"
								onChange={e =>
									multiSelectOnChange({
										value: 'includeIGMessenger',
										type: e.target.checked
											? FilterType.INCLUDES
											: FilterType.EXCLUDES
									})
								}
								defaultChecked={query?.includeIGMessenger}
								text="Has Instagram Messenger"
								hoverText="Filter for businesses that have an Instagram Messenger account"
							/>

							<Checkbox
								variant="chip"
								className="mr-3 mt-3"
								onChange={e =>
									multiSelectOnChange({
										value: 'includePhoneNumber',
										type: e.target.checked
											? FilterType.INCLUDES
											: FilterType.EXCLUDES
									})
								}
								defaultChecked={query?.includePhoneNumber}
								text="Has Phone Number"
								hoverText="Filter for businesses that have a phone number"
							/>

							<Checkbox
								variant="chip"
								className="mr-3 mt-3"
								onChange={e =>
									multiSelectOnChange({
										value: 'notesSaved',
										type: e.target.checked
											? FilterType.INCLUDES
											: FilterType.EXCLUDES
									})
								}
								defaultChecked={query?.notesSaved}
								text="Notes saved"
								hoverText="Filter for businesses that you have saved Notes for"
								lastPosition
							/>
						</div>
					</div>

					<div className="dashboard-container--controls-filter-contacted mt-8 flex h-full w-full flex-col justify-between">
						<span>
							<header className="dashboard-container--controls-filter-yelp-reviews-header text-xl font-light text-white">
								Number of Yelp reviews
							</header>
							<div className="flex flex-col gap-2 text-orange">
								<div className="flex items-center gap-3">
									<input
										placeholder="Min"
										type="number"
										min="0"
										value={yelpReviewsFilterMin}
										onChange={e =>
											setYelpReviewsFilterMin(
												Number(e.target.value)
											)
										}
									/>
									<button
										type="button"
										disabled={
											yelpReviewsApplyButtonDisabledCheck
										}
										onClick={
											yelpReviewsApplyButtonClickHandler
										}
									>
										Apply
									</button>
								</div>
								<div className="flex items-center gap-3">
									<input
										placeholder="Max"
										type="number"
										min="0"
										value={yelpReviewsFilterMax}
										onChange={e =>
											setYelpReviewsFilterMax(
												Number(e.target.value)
											)
										}
									/>
									<button
										type="button"
										disabled={
											yelpReviewsFilterMin === '' &&
											yelpReviewsFilterMax === ''
										}
										onClick={() => {
											yelpReviewsClearButtonClickHandler();
											yelpReviewsFilterChange(
												// @ts-ignore
												undefined,
												undefined
											);
										}}
									>
										Clear
									</button>
								</div>
							</div>
						</span>
					</div>

					<div className="dashboard-container--controls-filter-contacted mt-8 flex h-full w-full flex-col justify-between">
						<span className="flex items-center">
							<header className="dashboard-container--controls-filter-contacted-header text-xl font-light text-white">
								Contacted
							</header>
							<Switch
								className="dashboard-container--controls-filter-contacted-switch ml-auto"
								onChange={checked => {
									let type;

									if (checked) {
										type = FilterType.INCLUDES;
									} else if (checked === false) {
										type = FilterType.EXCLUDES;
									}

									multiSelectOnChange({
										value: 'contacted',
										type
									});
								}}
								value={
									query?.contactedYes !== undefined ||
									query?.contactedNo !== undefined
										? query?.contactedYes === true ||
											query?.contactedNo !== true
										: undefined
								}
								text={['No', 'Yes']}
							/>
							<p className="dashboard-container--controls-filter-contacted-hover-text-1">
								Filter for businesses based on contacted status
							</p>
							<p className="dashboard-container--controls-filter-contacted-hover-text-2">
								Filter for businesses based on contacted status
							</p>
						</span>
					</div>

					<div className="dashboard-container--controls-filter-contacted mt-8 flex h-full w-full flex-col justify-between">
						<span className="flex items-center">
							<header className="dashboard-container--controls-filter-contacted-header text-xl font-light text-white">
								Scheduled
							</header>
							<Switch
								className="dashboard-container--controls-filter-contacted-switch ml-auto"
								onChange={checked => {
									let type;

									if (checked) {
										type = FilterType.INCLUDES;
									} else if (checked === false) {
										type = FilterType.EXCLUDES;
									}

									multiSelectOnChange({
										value: 'scheduled',
										type
									});
								}}
								value={
									query?.scheduledYes !== undefined ||
									query?.scheduledNo !== undefined
										? query?.scheduledYes === true ||
											query?.scheduledNo !== true
										: undefined
								}
								text={['No', 'Yes']}
							/>
							<p className="dashboard-container--controls-filter-contacted-hover-text-1">
								Filter for businesses based on emailed scheduled
								status
							</p>
							<p className="dashboard-container--controls-filter-contacted-hover-text-2">
								Filter for businesses based on email scheduled
								status
							</p>
						</span>
					</div>

					<div className="dashboard-container--controls-filter-lead mt-8 flex h-full w-full flex-col justify-between">
						<span className="flex items-center">
							<span className="dashboard-container--controls-filter-lead-header text-xl font-light text-white">
								Lead
							</span>
							<Switch
								className="dashboard-container--controls-filter-lead-switch ml-auto"
								onChange={checked => {
									let type;

									if (checked) {
										type = FilterType.INCLUDES;
									} else if (checked === false) {
										type = FilterType.EXCLUDES;
									}

									multiSelectOnChange({
										value: 'lead',
										type
									});
								}}
								value={query?.lead}
							/>
							<p
								className={`dashboard-container--controls-filter-lead-hover-text-1 ${
									showAdvanced ? 'show-advanced' : ''
								}`}
							>
								Filter for businesses that have been marked as a
								Lead
							</p>
							<p
								className={`dashboard-container--controls-filter-lead-hover-text-2 ${
									showAdvanced ? 'show-advanced' : ''
								}`}
							>
								Filter for businesses that have been marked as a
								Lead
							</p>
						</span>
					</div>

					<div className="mt-5 flex h-full w-full flex-col justify-between">
						<button
							type="button"
							id="advanced-filters-button"
							onClick={() => setShowAdvanced(!showAdvanced)}
							className="cursor-pointer text-left text-xl font-light text-white"
						>
							Advanced filters
							<DropDown
								width={13}
								height={17}
								className={`ml-2 inline-block text-light-grey transition-transform transform${
									showAdvanced ? 'rotate-180' : ''
								}`}
							/>
						</button>
						{showAdvanced && (
							<div className="dashboard-container--controls-filter--row font-light">
								<Checkbox
									variant="chip"
									className="mr-3 mt-3"
									id="has-website-checkbox"
									onChange={e =>
										multiSelectOnChange({
											value: 'includeWebsitesYes',
											type: e.target.checked
												? FilterType.INCLUDES
												: FilterType.EXCLUDES
										})
									}
									defaultChecked={query?.includeWebsitesYes}
									text="Has website"
									advancedFilter
								/>
								<Checkbox
									variant="chip"
									className="mr-3 mt-3"
									onChange={e =>
										multiSelectOnChange({
											value: 'includeWebsitesNo',
											type: e.target.checked
												? FilterType.INCLUDES
												: FilterType.EXCLUDES
										})
									}
									defaultChecked={query?.includeWebsitesNo}
									text="No website"
									advancedFilter
								/>
								<Checkbox
									variant="chip"
									className="mr-3 mt-3"
									onChange={e =>
										multiSelectOnChange({
											value: 'includePOIs',
											type: e.target.checked
												? FilterType.INCLUDES
												: FilterType.EXCLUDES
										})
									}
									defaultChecked={query?.includePOIs}
									text="Has staff info"
									advancedFilter
								/>

								<Checkbox
									variant="chip"
									className="mr-3 mt-3"
									onChange={e =>
										multiSelectOnChange({
											value: 'countOne',
											type: e.target.checked
												? FilterType.INCLUDES
												: FilterType.EXCLUDES
										})
									}
									defaultChecked={query?.countOne}
									text="Locations: 1"
									advancedFilter
								/>

								<Checkbox
									variant="chip"
									className="mr-3 mt-3"
									onChange={e =>
										multiSelectOnChange({
											value: 'countTwoTen',
											type: e.target.checked
												? FilterType.INCLUDES
												: FilterType.EXCLUDES
										})
									}
									defaultChecked={query?.countTwoTen}
									text="Locations: 2 - 10"
									advancedFilter
								/>

								<Checkbox
									variant="chip"
									className="mr-3 mt-3"
									onChange={e =>
										multiSelectOnChange({
											value: 'countTenPlus',
											type: e.target.checked
												? FilterType.INCLUDES
												: FilterType.EXCLUDES
										})
									}
									defaultChecked={query?.countTenPlus}
									text="Locations: 10+"
									advancedFilter
								/>
							</div>
						)}
					</div>
				</div>
			</Popup>
		</div>
	);
}
